import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useCurrentWeek } from '../context/CurrentWeekContext';
import styles from '../styles/CalendarStatus.module.css';
import DatePicker from './DatePickerInput';

const CalendarStatus = () => {
  const [allCalendarData, setAllCalendarData] = useState([]);
  const [filteredCalendar, setFilteredCalendar] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameCounts, setGameCounts] = useState({});
  const [optimalBowlSplit, setOptimalBowlSplit] = useState(null);
  const [additionalBowlGames, setAdditionalBowlGames] = useState(0);
  const [editingDates, setEditingDates] = useState({});
  const { calendar, isLoading: contextLoading, error: contextError, fetchCurrentWeekAndCalendar } = useCurrentWeek();

  const fetchCalendar = useCallback(async () => {
    try {
      await fetchCurrentWeekAndCalendar();
    } catch (err) {
      console.error(err);
      setError('Failed to fetch calendar data');
    }
  }, [fetchCurrentWeekAndCalendar]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  useEffect(() => {
    if (calendar && !contextLoading) {
      setAllCalendarData(calendar);
      setLocalLoading(false);
    }
  }, [calendar, contextLoading]);

  useEffect(() => {
    if (allCalendarData.length > 0) {
      const uniqueYears = [...new Set(allCalendarData.map(week => week.season))];
      setYears(uniqueYears.sort((a, b) => b - a));
      setSelectedYear(uniqueYears[0]);
    }
  }, [allCalendarData]);

  const filterCalendarData = useCallback((year) => {
    const filtered = allCalendarData
      .filter(week => week.season === parseInt(year))
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    setFilteredCalendar(filtered);
  }, [allCalendarData]);

  useEffect(() => {
    if (selectedYear) {
      filterCalendarData(selectedYear);
    }
  }, [selectedYear, filterCalendarData]);

  // Fetch game counts for all weeks
  const fetchGameCounts = useCallback(async () => {
    if (!selectedYear || filteredCalendar.length === 0) return;

    const counts = {};
    for (const week of filteredCalendar) {
      try {
        const response = await api.get('/calendar/game-count', {
          params: {
            season: selectedYear,
            startDate: week.start_date,
            endDate: week.end_date,
            week: week.week,
            seasonType: week.season_type
          }
        });
        counts[`${week.week}-${week.season_type}`] = response.data.gameCount;
      } catch (err) {
        console.error(`Error fetching game count for week ${week.week}:`, err);
      }
    }
    setGameCounts(counts);
  }, [selectedYear, filteredCalendar]);

  useEffect(() => {
    fetchGameCounts();
  }, [fetchGameCounts]);

  // Fetch optimal bowl split
  const fetchOptimalBowlSplit = useCallback(async () => {
    if (!selectedYear) return;

    try {
      const response = await api.get('/calendar/optimal-bowl-split', {
        params: { 
          season: selectedYear,
          additionalGames: additionalBowlGames || undefined
        }
      });
      setOptimalBowlSplit(response.data);
    } catch (err) {
      console.error('Error fetching optimal bowl split:', err);
    }
  }, [selectedYear, additionalBowlGames]);

  useEffect(() => {
    fetchOptimalBowlSplit();
  }, [fetchOptimalBowlSplit]);

  // Get game count for a specific week (with real-time calculation for edited dates)
  const getGameCount = useCallback((week, editedStartDate, editedEndDate) => {
    const key = `${week.week}-${week.season_type}`;

    // If dates are being edited, calculate count in real-time
    if (editingDates[key]) {
      // Return cached count for now, will be updated on blur
      return gameCounts[key] || week.gameCount || 0;
    }

    return gameCounts[key] || week.gameCount || 0;
  }, [gameCounts, editingDates]);

  const updateWeekStart = async (newStartDate, weekData, manualOverride) => {
    try {
      await api.put('/calendar/update-week-start', {
        season: selectedYear,
        week: weekData.week,
        seasonType: weekData.season_type,
        newStartDate: newStartDate.toISOString(),
        manualOverride: manualOverride
      });
      fetchCalendar();
      fetchGameCounts();
    } catch (err) {
      setError('Failed to update week start date');
    }
  };

  const updateWeekEnd = async (newEndDate, weekData, manualOverride) => {
    try {
      await api.put('/calendar/update-week-end', {
        season: selectedYear,
        week: weekData.week,
        seasonType: weekData.season_type,
        newEndDate: newEndDate.toISOString(),
        manualOverride: manualOverride
      });
      fetchCalendar();
      fetchGameCounts();
      fetchOptimalBowlSplit();
    } catch (err) {
      setError('Failed to update week end date');
    }
  };

  const revertToAutomatic = async (weekData) => {
    try {
      await api.put('/calendar/revert-week-to-automatic', {
        season: selectedYear,
        week: weekData.week,
        seasonType: weekData.season_type
      });
      fetchCalendar();
      fetchGameCounts();
      fetchOptimalBowlSplit();
    } catch (err) {
      setError('Failed to revert week to automatic');
    }
  };

  const updateHideWeek = async (hideWeek, weekData) => {
    try {
      await api.put('/calendar/update-hide-week', {
        season: selectedYear,
        week: weekData.week,
        seasonType: weekData.season_type,
        hideWeek
      });
      fetchCalendar();
    } catch (err) {
      setError('Failed to update hide week status');
    }
  };

  const updateNotes = async (notes, weekData) => {
    try {
      await api.put('/calendar/update-notes', {
        season: selectedYear,
        week: weekData.week,
        notes
      });
      setAllCalendarData(prevData => 
        prevData.map(week => 
          week.season === selectedYear && week.week === weekData.week
            ? { ...week, notes }
            : week
        )
      );
    } catch (err) {
      setError('Failed to update notes');
    }
  };

  const updateMaxPicks = async (maxPicks, weekData) => {
    try {
      await api.put('/calendar/update-max-picks', {
        season: selectedYear,
        week: weekData.week,
        maxPicks: parseInt(maxPicks)
      });
      setAllCalendarData(prevData => 
        prevData.map(week => 
          week.season === selectedYear && week.week === weekData.week
            ? { ...week, max_picks: parseInt(maxPicks) }
            : week
        )
      );
    } catch (err) {
      setError('Failed to update max picks');
    }
  };

  const createCustomWeek = async () => {
    const lastWeek = filteredCalendar[filteredCalendar.length - 1];
    const newStartDate = new Date(lastWeek.end_date);
    newStartDate.setMinutes(newStartDate.getMinutes() + 1);
    
    const newEndDate = new Date(newStartDate);
    newEndDate.setDate(newEndDate.getDate() + 7);

    try {
      await api.post('/calendar/create-week', {
        season: selectedYear,
        week: lastWeek.week + 1,
        startDate: newStartDate.toISOString(),
        endDate: newEndDate.toISOString(),
        notes: 'Custom Week',
        maxPicks: 3
      });
      fetchCalendar();
    } catch (err) {
      setError('Failed to create custom week');
    }
  };

  const deleteCustomWeek = async (weekData) => {
    if (!weekData.user_added) {
      setError('Only custom weeks can be deleted');
      return;
    }

    try {
      await api.delete('/calendar/delete-week', {
        data: {
          season: selectedYear,
          week: weekData.week
        }
      });
      fetchCalendar();
    } catch (err) {
      setError('Failed to delete week');
    }
  };

  const handleNotesChange = (e, weekData) => {
    setFilteredCalendar(prevCalendar => 
      prevCalendar.map(week => 
        week.week === weekData.week && week.season_type === weekData.season_type
          ? { ...week, notes: e.target.value }
          : week
      )
    );
  };

  const handleDateChange = (date, type, weekData) => {
    const key = `${weekData.week}-${weekData.season_type}`;
    setEditingDates(prev => ({
      ...prev,
      [key]: { ...prev[key], [type]: date }
    }));
  };

  const handleDateBlur = (type, weekData) => {
    const key = `${weekData.week}-${weekData.season_type}`;
    const editedDate = editingDates[key]?.[type];
    
    if (editedDate) {
      const manualOverride = weekData[type === 'start' ? 'dynamic_week_start' : 'dynamic_week_end'] === 1;
      
      if (type === 'start') {
        updateWeekStart(editedDate, weekData, manualOverride);
      } else {
        updateWeekEnd(editedDate, weekData, manualOverride);
      }
      
      // Clear editing state
      setEditingDates(prev => {
        const newState = { ...prev };
        if (newState[key]) {
          delete newState[key][type];
          if (Object.keys(newState[key]).length === 0) {
            delete newState[key];
          }
        }
        return newState;
      });
    }
  };

  const applyOptimalSplit = async () => {
    if (!optimalBowlSplit?.optimalSplit) return;

    const week17 = filteredCalendar.find(w => w.week === 17 && w.season_type === 'postseason');
    if (!week17) return;

    try {
      await updateWeekEnd(new Date(optimalBowlSplit.optimalSplit), week17, true);
    } catch (err) {
      setError('Failed to apply optimal split');
    }
  };

  if (localLoading || contextLoading) return <div>Loading calendar status...</div>;
  if (error || contextError) return <div>{error || contextError}</div>;
  if (allCalendarData.length === 0) return <div>No calendar data available.</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>Calendar Status</h3>
        <select 
          className={styles.yearSelect}
          value={selectedYear} 
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        >
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Optimal Bowl Split Suggestion */}
      {optimalBowlSplit && optimalBowlSplit.optimalSplit && (
        <div className={styles.optimalSplitBanner}>
          <div className={styles.optimalSplitContent}>
            <strong>💡 Optimal Bowl Week Split Suggestion:</strong>
            <div className={styles.optimalSplitInputRow}>
              <label htmlFor="additionalBowlGames">
                Add games to Bowl Week 2 (CFP-dependent, NY6 bowls):
              </label>
              <input
                id="additionalBowlGames"
                type="number"
                min="0"
                value={additionalBowlGames}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10) || 0;
                  setAdditionalBowlGames(value);
                }}
                className={styles.additionalGamesInput}
              />
            </div>
            <span>Setting Bowl Week 1 end to <strong>{new Date(optimalBowlSplit.optimalSplit).toLocaleString()}</strong> would split {optimalBowlSplit.totalGames} games {optimalBowlSplit.additionalGamesCount > 0 ? `(${optimalBowlSplit.bowlGamesCount} current + ${optimalBowlSplit.additionalGamesCount} future)` : ''}:</span>
            <span>Week 17: {optimalBowlSplit.week17GameCount} games | Week 18: {optimalBowlSplit.week18GameCount} games (difference: {optimalBowlSplit.difference})</span>
            <button 
              className={styles.applyButton}
              onClick={applyOptimalSplit}
            >
              Apply Optimal Split
            </button>
          </div>
        </div>
      )}

      <table className={styles.statusTable}>
        <thead>
          <tr>
            <th className={styles.weekColumn}>Week</th>
            <th className={styles.notesColumn}>Notes</th>
            <th className={styles.dateColumn}>Start Date</th>
            <th className={styles.dateColumn}>End Date</th>
            <th className={styles.countColumn}>Games</th>
            <th className={styles.picksColumn}>Picks</th>
            <th className={styles.checkboxColumn}>Hide</th>
            <th className={styles.checkboxColumn}>Manual Override</th>
            <th className={styles.actionColumn}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredCalendar.map((week) => {
            const weekKey = `${week.week}-${week.season_type}`;
            const editing = editingDates[weekKey];
            const startDate = editing?.start ? editing.start : new Date(week.start_date);
            const endDate = editing?.end ? editing.end : new Date(week.end_date);
            const hasManualOverride = (week.dynamic_week_start === 1) || (week.dynamic_week_end === 1);
            const isBowlWeek = week.week >= 17 && week.season_type === 'postseason';

            return (
              <tr key={weekKey} className={hasManualOverride ? styles.manualOverrideRow : ''}>
                <td className={styles.weekColumn}>
                  {week.season_type === 'postseason' ? 'Post' : `Week ${week.week}`}
                  {isBowlWeek && <span className={styles.bowlBadge}>Bowl</span>}
                </td>
                <td className={styles.notesColumn}>
                  <input 
                    type="text" 
                    value={week.notes || ''} 
                    onChange={(e) => handleNotesChange(e, week)}
                    onBlur={() => updateNotes(week.notes, week)}
                  />
                </td>
                <td className={styles.dateColumn}>
                  <div>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => handleDateChange(date, 'start', week)}
                      onBlur={() => handleDateBlur('start', week)}
                      showTimeSelect
                      dateFormat="MMM d, yyyy h:mm aa"
                    />
                    <label className={styles.overrideCheckbox}>
                      <input
                        type="checkbox"
                        checked={week.dynamic_week_start === 1}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateWeekStart(startDate, week, true);
                          } else {
                            // Unchecking means revert to automatic
                            revertToAutomatic(week);
                          }
                        }}
                      />
                      <span>Lock</span>
                    </label>
                  </div>
                </td>
                <td className={styles.dateColumn}>
                  <div>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => handleDateChange(date, 'end', week)}
                      onBlur={() => handleDateBlur('end', week)}
                      showTimeSelect
                      dateFormat="MMM d, yyyy h:mm aa"
                    />
                    <label className={styles.overrideCheckbox}>
                      <input
                        type="checkbox"
                        checked={week.dynamic_week_end === 1}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateWeekEnd(endDate, week, true);
                          } else {
                            // Unchecking means revert to automatic
                            revertToAutomatic(week);
                          }
                        }}
                      />
                      <span>Lock</span>
                    </label>
                  </div>
                </td>
                <td className={styles.countColumn}>
                  {getGameCount(week, editing?.start?.toISOString(), editing?.end?.toISOString())}
                </td>
                <td className={styles.picksColumn}>
                  <input 
                    type="number" 
                    min="1"
                    max="10"
                    value={week.max_picks || 1} 
                    onChange={(e) => updateMaxPicks(e.target.value, week)}
                    style={{ width: '50px' }}
                  />
                </td>
                <td className={styles.checkboxColumn}>
                  <input 
                    type="checkbox" 
                    checked={week.hide_week === 1} 
                    onChange={(e) => updateHideWeek(e.target.checked, week)}
                  />
                </td>
                <td className={styles.checkboxColumn}>
                  <input 
                    type="checkbox" 
                    checked={hasManualOverride}
                    disabled
                    title={hasManualOverride ? 'This week has manual overrides enabled' : 'No manual overrides'}
                  />
                </td>
                <td className={styles.actionColumn}>
                  {hasManualOverride && (
                    <button 
                      className={styles.revertButton}
                      onClick={() => revertToAutomatic(week)}
                      title="Revert to automatic control"
                    >
                      Revert
                    </button>
                  )}
                  {week.user_added === 1 && (
                    <button 
                      className={styles.deleteButton}
                      onClick={() => deleteCustomWeek(week)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button 
        className={styles.createButton}
        onClick={createCustomWeek}
      >
        Add Custom Week
      </button>
    </div>
  );
};

export default CalendarStatus;
